import { useState, useEffect } from 'react';
import PublicLayout from '../../components/layout/PublicLayout';
import useSEO from '../../hooks/useSEO';
import ReactPlayer from 'react-player';
import { radioAPI } from '../../utils/api';
import api from '../../utils/api';

const terrestrialAPI = {
  getAll: () => api.get('/terrestrial-stations'),
};

const REGIONS = [
  'North Central','North East','North West',
  'South East','South South','South West','FCT',
];

// Region colour map
const REGION_COLORS = {
  'North Central': 'from-navy-700 to-navy-900',
  'North East':    'from-[#1a3a1a] to-[#0a2a0a]',
  'North West':    'from-[#2a1a3a] to-[#1a0a2a]',
  'South East':    'from-[#3a1a00] to-[#2a0a00]',
  'South South':   'from-[#003a3a] to-[#001a2a]',
  'South West':    'from-[#1a1a3a] to-[#0a0a2a]',
  'FCT':           'from-[#2a2a1a] to-[#1a1a0a]',
};

// ── JNF Online Radio Player ────────────────────────────────────
function JNFRadioPlayer({ radio }) {
  const [playing, setPlaying] = useState(false);
  const [error, setError]     = useState(false);

  return (
    <div className="bg-gradient-to-br from-navy-800 to-navy-900 rounded-3xl overflow-hidden shadow-2xl">
      {/* Header */}
      <div className="px-8 py-7 border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="relative">
            <img src="/logo.png" alt="JNF" className="h-16 w-16 rounded-full border-2 border-cyan-400 object-cover"
              onError={e => e.target.style.display='none'} />
            {radio?.is_live && (
              <span className="absolute -bottom-1 -right-1 h-4 w-4 bg-red-500 rounded-full border-2 border-navy-800 animate-pulse" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <h2 className="text-white font-bold text-xl">{radio?.station_name || 'JNF Radio'}</h2>
              {radio?.is_live && (
                <span className="bg-red-500/20 border border-red-400/40 text-red-300 text-xs font-bold px-2 py-0.5 rounded-full">
                  LIVE
                </span>
              )}
            </div>
            <p className="text-white/50 text-sm">{radio?.description || 'Official JNF online radio broadcast'}</p>
          </div>
        </div>
      </div>

      {/* Player */}
      <div className="px-8 py-7 text-center">
        {radio?.stream_url ? (
          <>
            <ReactPlayer url={radio.stream_url} playing={playing}
              onError={() => { setError(true); setPlaying(false); }}
              width="0" height="0" />

            <button onClick={() => { setError(false); setPlaying(p => !p); }}
              className={`h-20 w-20 mx-auto rounded-full flex items-center justify-center text-3xl transition-all shadow-xl mb-4 ${
                playing ? 'bg-red-500 hover:bg-red-600 scale-110' : 'bg-cyan-400 hover:bg-cyan-300'
              }`}>
              {playing ? '⏸' : '▶'}
            </button>

            {error && <p className="text-red-400 text-sm mb-3">Stream currently unavailable. Please try again later.</p>}
            <p className="text-white/50 text-sm mb-4">{playing ? '🔊 You are listening to JNF Radio...' : 'Click to start listening'}</p>

            {playing && !error && (
              <div className="flex justify-center gap-1.5 mb-4">
                {[...Array(9)].map((_,i) => (
                  <div key={i} className="w-1 bg-cyan-400 rounded-full animate-pulse"
                    style={{ height:`${8+(i%5)*5}px`, animationDelay:`${i*0.1}s`, animationDuration:'0.7s' }} />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="py-6">
            <div className="text-5xl mb-3">📡</div>
            <p className="text-white/50 text-sm">Stream not yet configured.</p>
            <p className="text-white/30 text-xs mt-1">Admin: set stream URL in Radio settings.</p>
          </div>
        )}

        {/* Schedule */}
        {radio?.schedule_info && (
          <div className="bg-white/5 rounded-xl p-4 text-left mt-2">
            <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wider mb-2">📅 Broadcast Schedule</p>
            <p className="text-white/60 text-sm whitespace-pre-line">{radio.schedule_info}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Terrestrial stations section ──────────────────────────────
function TerrestrialSection({ grouped, regions, activeRegion, setActiveRegion }) {
  const [activeState, setActiveState] = useState(null);

  const states = activeRegion && grouped[activeRegion]
    ? Object.keys(grouped[activeRegion]).sort()
    : [];

  useEffect(() => { setActiveState(null); }, [activeRegion]);

  const stationsToShow = activeRegion && activeState && grouped[activeRegion]?.[activeState]
    ? grouped[activeRegion][activeState]
    : activeRegion
    ? Object.values(grouped[activeRegion] || {}).flat()
    : [];

  return (
    <div>
      {/* Region tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
        {regions.filter(r => grouped[r]).map(r => (
          <button key={r} onClick={() => setActiveRegion(r)}
            className={`flex-shrink-0 px-4 py-2 rounded-xl text-xs font-semibold transition-all border ${
              activeRegion === r
                ? 'bg-navy-700 text-white border-navy-700'
                : 'bg-white border-gray-200 text-gray-600 hover:border-navy-300'
            }`}>
            {r} ({Object.values(grouped[r] || {}).flat().length})
          </button>
        ))}
      </div>

      {activeRegion && states.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2 mb-5">
          <button onClick={() => setActiveState(null)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
              !activeState ? 'bg-cyan-400 text-navy-900 border-cyan-400' : 'bg-white border-gray-200 text-gray-500'
            }`}>
            All States
          </button>
          {states.map(st => (
            <button key={st} onClick={() => setActiveState(st)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                activeState === st ? 'bg-cyan-400 text-navy-900 border-cyan-400' : 'bg-white border-gray-200 text-gray-500'
              }`}>
              {st} ({grouped[activeRegion][st].length})
            </button>
          ))}
        </div>
      )}

      {/* Station cards */}
      {stationsToShow.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {stationsToShow.map(s => (
            <div key={s.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
              <div className={`bg-gradient-to-r ${REGION_COLORS[s.region] || 'from-navy-700 to-navy-900'} px-5 py-4`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white font-bold text-base leading-tight">{s.name}</p>
                    <span className="inline-block bg-cyan-400/20 border border-cyan-400/30 text-cyan-300 text-xs font-bold px-2 py-0.5 rounded-full mt-1">
                      📻 {s.frequency}
                    </span>
                  </div>
                  {s.city && <p className="text-white/50 text-xs text-right">📍 {s.city}<br/>{s.state}</p>}
                </div>
              </div>

              <div className="p-4 space-y-2">
                {s.programme_name && (
                  <p className="font-semibold text-navy-700 text-sm">{s.programme_name}</p>
                )}
                {s.description && (
                  <p className="text-gray-500 text-xs leading-relaxed">{s.description}</p>
                )}
                {(s.broadcast_day || s.broadcast_time) && (
                  <div className="bg-navy-50 rounded-xl p-2.5 text-xs">
                    {s.broadcast_day && (
                      <div className="flex gap-2 mb-1">
                        <span className="text-navy-400 font-medium w-12">Day</span>
                        <span className="text-navy-700 font-semibold">{s.broadcast_day}</span>
                      </div>
                    )}
                    {s.broadcast_time && (
                      <div className="flex gap-2">
                        <span className="text-navy-400 font-medium w-12">Time</span>
                        <span className="text-navy-700 font-bold">{s.broadcast_time}</span>
                      </div>
                    )}
                  </div>
                )}
                {s.contact_phone && (
                  <a href={`tel:${s.contact_phone}`} className="text-cyan-600 text-xs hover:underline block">
                    📞 {s.contact_phone}
                  </a>
                )}
                {s.website_url && (
                  <a href={s.website_url} target="_blank" rel="noopener noreferrer"
                    className="text-cyan-600 text-xs hover:underline block truncate">
                    🌐 {s.website_url}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        !activeRegion && (
          <div className="text-center py-12 text-gray-400">
            <div className="text-4xl mb-3">📡</div>
            <p className="font-medium">Select a region above to browse stations</p>
          </div>
        )
      )}
    </div>
  );
}

// ── Main Radio Page ───────────────────────────────────────────
export default function RadioPage() {
  useSEO({
    title: 'Radio — JNF',
    description: 'Listen to JNF Radio online and find Tijaniyyah programming on local Nigerian radio stations across all states.',
    url: '/radio',
  });

  const [radio, setRadio]           = useState(null);
  const [grouped, setGrouped]       = useState({});
  const [regions, setRegions]       = useState([]);
  const [activeRegion, setActiveRegion] = useState('');
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState('online'); // 'online' | 'terrestrial'

  useEffect(() => {
    Promise.all([
      radioAPI.getConfig(),
      terrestrialAPI.getAll(),
    ]).then(([r, t]) => {
      setRadio(r.data.data || null);
      setGrouped(t.data.grouped || {});
      const avail = (t.data.regions || []).filter(reg => t.data.grouped?.[reg]);
      setRegions(avail);
      if (avail.length) setActiveRegion(avail[0]);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <PublicLayout>
      <div className="max-w-7xl mx-auto px-4 py-12">

        {/* Page header */}
        <div className="mb-8">
          <h1 className="section-title mb-2">📻 Radio</h1>
          <p className="text-gray-500">JNF online radio and Tijaniyyah programming on local Nigerian stations</p>
        </div>

        {/* Tab switcher */}
        <div className="flex gap-2 mb-8 bg-gray-100 p-1 rounded-2xl w-fit">
          {[
            { key: 'online',      label: '🌐 JNF Online Radio'  },
            { key: 'terrestrial', label: '📡 Local Radio Stations' },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                activeTab === tab.key
                  ? 'bg-white text-navy-800 shadow-sm'
                  : 'text-gray-500 hover:text-navy-600'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-10 w-10 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* ── Section 1: JNF Online Radio ── */}
            {activeTab === 'online' && (
              <div className="max-w-2xl mx-auto">
                <JNFRadioPlayer radio={radio} />
                <div className="mt-6 bg-cyan-50 border border-cyan-100 rounded-2xl p-5 text-sm text-cyan-800">
                  <p className="font-semibold mb-1">🎙 About JNF Radio</p>
                  <p className="text-cyan-700 leading-relaxed">
                    JNF Radio is the official online broadcast station of Jamuiyat Nasir Al-Fridaous Al Tijaniyyah.
                    We broadcast Qasidas, Wird, Islamic lectures, and special Tijaniyyah programmes 24/7.
                    Tune in from anywhere in the world.
                  </p>
                </div>
              </div>
            )}

            {/* ── Section 2: Terrestrial stations ── */}
            {activeTab === 'terrestrial' && (
              <div>
                <div className="bg-navy-50 border border-navy-100 rounded-2xl p-5 mb-6 flex gap-4 items-start">
                  <div className="text-3xl flex-shrink-0">📡</div>
                  <div>
                    <p className="font-semibold text-navy-700 mb-1">Partner Radio Stations</p>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      These local Nigerian radio stations carry dedicated JNF and Tijaniyyah programming.
                      Tune in at the listed times for Wird, Qasidas, and Islamic lectures on your local FM/AM frequency.
                    </p>
                  </div>
                </div>

                <TerrestrialSection
                  grouped={grouped}
                  regions={regions}
                  activeRegion={activeRegion}
                  setActiveRegion={setActiveRegion}
                />

                {/* Contact CTA */}
                <div className="mt-10 bg-white border border-gray-100 rounded-2xl p-6 flex gap-4 shadow-sm">
                  <div className="text-3xl flex-shrink-0">📻</div>
                  <div>
                    <p className="font-semibold text-navy-700 mb-1">Is your station missing?</p>
                    <p className="text-gray-500 text-sm">
                      If your radio station carries JNF or Tijaniyyah programming and is not listed,{' '}
                      <a href="/contact" className="text-cyan-600 font-medium hover:underline">contact us</a> to add it.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </PublicLayout>
  );
}
